import { flattenNavV2, itemMatchesPrograms, type ProgramTag, type ProviderType } from './nav-v2';
import type { ProviderProgramContext } from './provider-type';
import { matchGeneratedProgramRoutePolicy } from '@statura/experience-contract';

function normalizeHref(href: string): string {
  const path = href.split(/[?#]/, 1)[0]?.replace(/\/+$/, '') || '/';
  return path.startsWith('/') ? path : `/${path}`;
}

const NAV_PROGRAMS_BY_HREF = new Map<string, readonly ProgramTag[]>(
  flattenNavV2()
    .filter((item) => item.programs && item.programs.length > 0)
    .map((item) => [normalizeHref(item.href), item.programs ?? []]),
);

export function programTagsForHref(href: string): readonly ProgramTag[] {
  const normalized = normalizeHref(href);
  // The QI child pages were folded into query tabs. Keep the RAC firewall on
  // their new page path until the generated route inventory is refreshed.
  if (normalized === '/compliance/quality-indicators') return ['RAC'];
  const declared = NAV_PROGRAMS_BY_HREF.get(normalized);
  if (declared) return declared;
  const policy = matchGeneratedProgramRoutePolicy('web', normalized);
  if (policy?.classification === 'SAH_ONLY') return ['SAH'];
  if (policy?.classification === 'RAC_ONLY') return ['RAC'];
  return [];
}

type ProgramRouteProvider = ProviderType | ProviderProgramContext;

function isFirewallFamily(value: string): value is 'SAH' | 'RAC' | 'OTHER' {
  return value === 'SAH' || value === 'RAC' || value === 'OTHER';
}

function admittedFirewalls(
  provider: ProgramRouteProvider,
): readonly ('SAH' | 'RAC' | 'OTHER')[] | null {
  if (typeof provider === 'string' || provider.workspace == null) return null;
  return provider.admittedFirewalls ?? [];
}

/** Full server projection only: cached labels, URL hints and UNKNOWN are not
 * empty-provider evidence. Callers must also supply their current actor tenant.
 * This presentation rule does not replace actor/module/trial/data authority.
 */
export function isConfirmedEmptyProviderContext(
  context: ProgramRouteProvider,
  currentTenantId?: string | null,
): boolean {
  return (
    typeof context !== 'string' &&
    Boolean(currentTenantId) &&
    context.tenantId === currentTenantId &&
    context.discoveryStatus === 'CONFIRMED_EMPTY' &&
    context.providerType === 'UNKNOWN' &&
    context.resolutionStatus === 'UNKNOWN' &&
    context.activeProgramFamily === null &&
    context.eligibleProgramFamilies.length === 0 &&
    context.lensOptions.length === 0 &&
    !context.selectionRequired &&
    !context.selectionInvalid &&
    context.selectionSource === 'NONE' &&
    /^[a-f0-9]{16}$/.test(context.authorityRevision) &&
    context.contextRevision === `${context.authorityRevision}:CONFIRMED_EMPTY`
  );
}

/** One actor-bound empty-workspace rule for workforce reads and commands.
 * Registered setup retains its declared family, but has no client authority. */
export function isEmptyProviderWorkspaceContext(
  context: ProgramRouteProvider,
  currentTenantId?: string | null,
): boolean {
  if (isConfirmedEmptyProviderContext(context, currentTenantId)) return true;
  if (
    typeof context === 'string' ||
    !currentTenantId ||
    context.tenantId !== currentTenantId ||
    context.discoveryStatus !== 'CONFIRMED_EMPTY'
  )
    return false;
  const family = context.providerType;
  // A resident-free residential facility still needs its wing/client-service
  // demand board. Never substitute the workforce-only view for that programme.
  if (family !== 'SAH') return false;
  return (
    context.resolutionStatus === 'RESOLVED_SINGLE' &&
    context.activeProgramFamily === family &&
    context.workspace === 'HOME_COMMUNITY' &&
    context.workspaceFamilies?.length === 1 &&
    context.workspaceFamilies[0] === family &&
    context.admittedFirewalls?.length === 1 &&
    context.admittedFirewalls[0] === family &&
    context.eligibleProgramFamilies.length === 1 &&
    context.eligibleProgramFamilies[0] === family &&
    context.lensOptions.length === 1 &&
    context.lensOptions[0]?.family === family &&
    context.lensOptions[0]?.firewallFamily === family &&
    !context.selectionRequired &&
    !context.selectionInvalid &&
    context.selectionSource === 'AUTO' &&
    /^[a-f0-9]{16}$/.test(context.authorityRevision) &&
    context.contextRevision === `${context.authorityRevision}:${family}_SETUP`
  );
}

/** A wildcard is admissible only as the compiler's exact single-parameter
 * page template. Recursive/catchall/generic wildcard policies never qualify. */
function exactEmptyProviderRoute(
  policy: NonNullable<ReturnType<typeof matchGeneratedProgramRoutePolicy>>,
  href: string,
): boolean {
  const path = normalizeHref(href);
  if (!policy.route.includes('*')) return policy.route === path;
  const source = 'emptyProviderSourceFile' in policy ? policy.emptyProviderSourceFile : undefined;
  const prefix = 'apps/web/src/app/(dashboard)/';
  if (
    typeof source !== 'string' ||
    !source.startsWith(prefix) ||
    !/\/page\.[jt]sx?$/.test(source) ||
    !policy.sourceFiles.includes(source) ||
    policy.route.includes('**')
  )
    return false;
  const segments = source
    .slice(prefix.length)
    .replace(/\/page\.[jt]sx?$/, '')
    .split('/')
    .filter((part) => !/^\([^/]+\)$/.test(part));
  if (
    !segments.length ||
    segments.some(
      (part) => !/^[A-Za-z0-9_-]+$/.test(part) && !/^\[[A-Za-z][A-Za-z0-9_]*\]$/.test(part),
    )
  )
    return false;
  const pattern = '/' + segments.map((part) => (part.startsWith('[') ? '*' : part)).join('/');
  const actual = path.slice(1).split('/');
  return (
    pattern === policy.route &&
    actual.length === segments.length &&
    segments.every((part, index) =>
      part.startsWith('[') ? Boolean(actual[index]) : part === actual[index],
    )
  );
}

export function isHrefAllowedByGeneratedProgramPolicy(
  href: string,
  provider: ProgramRouteProvider,
  currentTenantId?: string | null,
): boolean {
  const providerType = typeof provider === 'string' ? provider : provider.providerType;
  const admitted = admittedFirewalls(provider);
  const normalized = normalizeHref(href);
  // This administration child has exactly the fleet register's programme
  // admission. Keep its own admin/Auth/runtime gates. The source taxonomy also
  // declares it; refreshing the global generated inventory belongs to the
  // later integration pass, without changing unrelated route policies here.
  if (normalized === '/admin/system/mobile-devices/shared-stations')
    return isHrefAllowedByGeneratedProgramPolicy(
      '/admin/system/mobile-devices',
      provider,
      currentTenantId,
    );
  // The shipped B2G audit page shares the DHDA connection's programme scope.
  // Retain its own role, entitlement and parent-switch guards; the generated
  // route inventory predates this leaf (as with the QI alias below).
  if (normalized === '/compliance/b2g/audit')
    return isHrefAllowedByGeneratedProgramPolicy(
      '/settings/integrations/dhda',
      provider,
      currentTenantId,
    );
  // The internal SYSTEM_ADMIN new payroll activation control shares the payroll
  // settings hub's programme scope. Its route-role, platform guard and leaf
  // layout still decide access; the generated route inventory predates this leaf.
  if (normalized === '/settings/payroll/activation')
    return isHrefAllowedByGeneratedProgramPolicy('/settings/payroll', provider, currentTenantId);
  // Quality indicators are a residential (RAC) obligation; a mixed workspace
  // that admits RAC reaches them too.
  if (normalized === '/compliance/quality-indicators') {
    return providerType === 'RAC' || (admitted?.includes('RAC') ?? false);
  }
  const policy = matchGeneratedProgramRoutePolicy('web', normalized);
  if (!policy) return false;
  const emptyProof =
    'emptyProviderSemanticProofId' in policy ? policy.emptyProviderSemanticProofId : undefined;
  if (
    isEmptyProviderWorkspaceContext(provider, currentTenantId) &&
    policy.classification === 'CONTEXT_REQUIRED' &&
    typeof emptyProof === 'string' &&
    /^EXP-SHARED-[A-Z0-9-]+$/.test(emptyProof) &&
    policy.firewallFamilies.length === 3 &&
    ['SAH', 'RAC', 'OTHER'].every((family) => policy.firewallFamilies.includes(family)) &&
    exactEmptyProviderRoute(policy, href)
  )
    return true;
  if (policy.classification === 'PROGRAM_NEUTRAL') {
    // Tenant administration needs no programme enrolment, but this bounded fix
    // preserves the existing chooser for tenants awaiting an explicit selection.
    if (
      policy.sharedSemanticProofId === 'EXP-SHARED-TENANT-ADMINISTRATION' &&
      typeof provider !== 'string' &&
      provider.selectionRequired
    )
      return false;
    return Boolean(policy.sharedSemanticProofId);
  }
  const isContributionsRoute =
    normalized === '/finance/contributions' || normalized.startsWith('/finance/contributions/');
  const restrictOtherOnlyContributions = (allowed: boolean): boolean => {
    if (!allowed || !isContributionsRoute) return allowed;
    const admittedBySah = admitted?.includes('SAH') ?? providerType === 'SAH';
    if (admittedBySah) return true;
    if (typeof provider === 'string') return false;
    // Eligible families are resolved from actual enrolments, but CHSP is only
    // admitted through the selected OTHER workspace — never residential.
    const admittedByOther = admitted ? admitted.includes('OTHER') : providerType === 'OTHER';
    return admittedByOther && provider.eligibleProgramFamilies.includes('CHSP');
  };
  // Keep legacy byte-for-byte semantics where no workspace has been resolved.
  if (admitted) {
    const allowed =
      policy.classification === 'SAH_ONLY'
        ? admitted.includes('SAH')
        : policy.classification === 'RAC_ONLY'
          ? admitted.includes('RAC')
          : policy.classification === 'OTHER_PROGRAM'
            ? admitted.includes('OTHER')
            : policy.firewallFamilies.some(
                (family) => isFirewallFamily(family) && admitted.includes(family),
              );
    return restrictOtherOnlyContributions(allowed);
  }
  const allowed =
    policy.classification === 'SAH_ONLY'
      ? providerType === 'SAH'
      : policy.classification === 'RAC_ONLY'
        ? providerType === 'RAC'
        : policy.classification === 'OTHER_PROGRAM'
          ? providerType === 'OTHER'
          : isFirewallFamily(providerType) && policy.firewallFamilies.includes(providerType);
  return restrictOtherOnlyContributions(allowed);
}

/**
 * 2026-09-06: when a destination is declared for exactly one of the tenant's
 * eligible programme families, that family is the only workspace the destination
 * can open in, so the shell selects it instead of asking. Shared destinations
 * (no declared programme) and destinations matching several eligible families
 * still require an explicit choice.
 */
export function autoSelectableProgramFamilyForHref<
  T extends { readonly family: string; readonly firewallFamily: string },
>(href: string, lensOptions: readonly T[]): T['family'] | null {
  const tags = programTagsForHref(href);
  if (tags.length === 0) {
    const normalized = normalizeHref(href);
    // Contributions is now a shared SAH/CHSP entry. Preserve main's single-SAH
    // shortcut, but never infer SAH when CHSP also needs an explicit choice.
    if (
      normalized === '/finance/contributions' &&
      !lensOptions.some((option) => option.family === 'CHSP')
    ) {
      const sahOptions = lensOptions.filter((option) => option.firewallFamily === 'SAH');
      return sahOptions.length === 1 ? sahOptions[0]!.family : null;
    }
    return null;
  }
  const matches = lensOptions.filter((option) =>
    (tags as readonly string[]).includes(option.firewallFamily),
  );
  return matches.length === 1 ? matches[0]!.family : null;
}

export function isHrefVisibleForProviderProgram(
  href: string,
  provider: ProgramRouteProvider,
  declaredPrograms?: readonly ProgramTag[],
  currentTenantId?: string | null,
): boolean {
  if (!isHrefAllowedByGeneratedProgramPolicy(href, provider, currentTenantId)) return false;
  return itemMatchesPrograms({ programs: declaredPrograms ?? programTagsForHref(href) }, provider);
}
